import React, { useCallback, useState } from "react";

import WarningImage from "@common/assets/images/warning.png";
import { AdministrationCard } from "@common/components/AdministrationCard/AdministrationCard";
import Button from "@common/components/Button/Button";
import { useModal } from "@common/components/Modal/Modal";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { UserData, Workspace, WorkspaceData } from "@common/models";
import { Invitation, isAdmin, Member, MemberRole } from "@common/models/Member.model";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { helpCenterArticles } from "@common/util/helpCenterArticles";
import identifiers from "@common/util/identifiers.json";
import { showIntercomArticle } from "@common/util/intercom";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useAuthContext } from "@main/context/Auth/Auth.context";
import { TeamMemberGroupHeader } from "@main/pages/Administration/components/TeamMembersCard/TeamMemberHeaders/TeamMemberGroupHeader";
import { TeamMemberAiAgentRow } from "@main/pages/Administration/components/TeamMembersCard/TeamMemberRows/TeamMemberAiAgentRow";
import { TeamMemberInvitationRow } from "@main/pages/Administration/components/TeamMembersCard/TeamMemberRows/TeamMemberInvitationRow";
import { TeamMemberRow } from "@main/pages/Administration/components/TeamMembersCard/TeamMemberRows/TeamMemberRow";
import * as Styled from "@main/pages/Administration/components/TeamMembersCard/TeamMembersCard.styles";
import { useIntl } from "react-intl";

import CustomizeTeamMemberModalBody from "../ModalBody/CustomizeTeamMember";
import InviteMembersModalBody from "../ModalBody/InviteMembersModalBody";

export interface Props {
  teamData: WorkspaceData | undefined;
  deleteMember: (userID: string) => Promise<void>;
  deleteMemberInvitation: (invitation: Invitation) => Promise<void>;
  inviteMembers: (emails: string[], role: MemberRole, appIds?: string[]) => Promise<void>;
  resendInvite: (invite: Invitation) => Promise<void>;
  customizeTeamMember: (role: MemberRole, selectedMember: Member, apps: string[]) => Promise<void>;
  deleteCurrentMember: (userID: string) => Promise<void>;
  loading: boolean;
}

export const inviteModalHash = "#invite";

export default function TeamMembersCard({
  teamData,
  deleteMember,
  deleteMemberInvitation,
  inviteMembers,
  resendInvite,
  customizeTeamMember,
  deleteCurrentMember,
  loading,
}: Props) {
  const { userData } = useAuthContext();
  const { selectedWorkspace } = useAppSelectionContext();
  const isUserAdmin = isAdmin(selectedWorkspace?.role);

  const [selectedMember, setSelectedMember] = useState<Member>();
  const [selectedInvite, setSelectedInvite] = useState<Invitation>();

  const [searchQuery, setSearchQuery] = useState("");

  const {
    Modal: InviteMembersModal,
    toggle: toggleInvite,
    modalProps: inviteMembersInternal,
  } = useModal({ size: "full", hash: inviteModalHash, allowOpen: isUserAdmin });

  const {
    Modal: CustomizeRoleModal,
    toggle: toggleCustomize,
    modalProps: customizeRoleInternal,
  } = useModal({ size: "full" });
  const {
    Modal: DeleteMemberModal,
    toggle: toggleDelete,
    modalProps: deleteMemberInternal,
  } = useModal({ size: "narrow" });
  const {
    Modal: CancelInviteModal,
    toggle: toggleCancelInvite,
    modalProps: cancelInviteInternal,
  } = useModal({ size: "narrow" });

  // todo this needs to be refactored in the scope of some other task
  const intl = useIntl();
  const t = (id: string) => {
    return intl.formatMessage({ id });
  };

  const didPressDeleteMember = (member: Member) => {
    setSelectedMember(member);
    toggleDelete();
  };

  const handleDeleteMember = (member: Member) => {
    const isCurrentUser = userData?.id === member.user.id;
    if (isCurrentUser) {
      deleteCurrentMember(member.user.id).then(() => {
        toggleDelete();
      });
    } else {
      deleteMember(member.user.id).then(() => {
        toggleDelete();
      });
    }
  };

  const didPressCustomizeMember = (member: Member) => {
    setSelectedMember(member);
    toggleCustomize();
  };

  const handleCustomizeTeamMember = useCallback(
    (role: MemberRole, apps: string[]) => {
      if (!selectedMember) return;

      customizeTeamMember(role, selectedMember, apps);
      toggleCustomize();
      setSelectedMember(undefined);
    },
    [customizeTeamMember, toggleCustomize, selectedMember],
  );

  const didPressCancelInvite = (invite: Invitation) => {
    setSelectedInvite(invite);
    toggleCancelInvite();
  };

  const handleDeleteMemberInvitation = useCallback(
    (invitation: Invitation) => {
      deleteMemberInvitation(invitation).then(() => {
        toggleCancelInvite();
      });
    },
    [deleteMemberInvitation, toggleCancelInvite],
  );

  const handleResendInvite = async (invite: Invitation) => {
    resendInvite(invite);
  };

  const teamMemberGroups: TeamMembersGroup[] = groupTeamMembers(searchQuery, teamData, selectedWorkspace, userData);
  const aiAgentsGroup: TeamMembersGroup | undefined = teamMemberGroups.find((g) => g.id === GROUP_ID_AI);
  const adminsGroup: TeamMembersGroup | undefined = teamMemberGroups.find((g) => g.id === GROUP_ID_ADMINS);
  const membersGroup: TeamMembersGroup | undefined = teamMemberGroups.find((g) => g.id === GROUP_ID_MEMBERS);

  return (
    <>
      <AdministrationCard
        title={"My team"}
        subtitle={"Manage users from here. All seats are free."}
        testId={identifiers["members.myTeam.header"]}
        topLeftAccessory={
          <Button
            onClick={toggleInvite}
            size="small"
            disabled={!isUserAdmin}
            tooltip={{
              text: t("administration.members.teamMembers.button_tooltip"),
              position: "top",
              disabled: isUserAdmin,
            }}
            testId={identifiers["members.myTeam.button.invite"]}
          >
            {t("administration.members.teamMembers.button")}
          </Button>
        }
      >
        <Styled.MembersSearch
          value={searchQuery}
          onChanged={(text) => setSearchQuery(text)}
        />
        {aiAgentsGroup && "sheldon".includes(searchQuery.toLowerCase()) ? (
          <>
            <TeamMemberGroupHeader
              title={aiAgentsGroup.name}
              color1={aiAgentsGroup.color1}
            />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <TeamMemberAiAgentRow />
            </div>
          </>
        ) : null}
        {adminsGroup && (adminsGroup.members.length > 0 || adminsGroup.invitations.length > 0) ? (
          <>
            <TeamMemberGroupHeader
              title={adminsGroup.name}
              color1={adminsGroup.color1}
            />
            <div style={{ display: "flex", flexDirection: "column" }}>
              {adminsGroup.members.map((member) => (
                <TeamMemberRow
                  key={member.id}
                  member={member}
                  currentUserIsAdmin={isUserAdmin}
                  currentUserId={userData?.id || ""}
                  onSelectDeleteMember={() => didPressDeleteMember(member)}
                  onSelectCustomizeMember={() => didPressCustomizeMember(member)}
                />
              ))}
              {userData &&
                selectedWorkspace?.role !== MemberRole.GUEST &&
                adminsGroup.invitations.map((invite) => (
                  <TeamMemberInvitationRow
                    key={invite.id}
                    invitation={invite}
                    currentUserIsAdmin={isUserAdmin}
                    onCancelInvite={() => didPressCancelInvite(invite)}
                    handleResendInvite={() => handleResendInvite(invite)}
                  />
                ))}
            </div>
          </>
        ) : null}

        {membersGroup && (membersGroup.members.length > 0 || membersGroup.invitations.length > 0) ? (
          <>
            <TeamMemberGroupHeader
              title={membersGroup.name}
              color1={membersGroup.color1}
              color2={membersGroup.color2}
            />
            <div style={{ display: "flex", flexDirection: "column" }}>
              {membersGroup.members.map((member) => (
                <TeamMemberRow
                  key={member.id}
                  member={member}
                  currentUserIsAdmin={isUserAdmin}
                  currentUserId={userData?.id || ""}
                  onSelectDeleteMember={() => didPressDeleteMember(member)}
                  onSelectCustomizeMember={() => didPressCustomizeMember(member)}
                />
              ))}
              {selectedWorkspace?.role !== MemberRole.GUEST &&
                membersGroup.invitations?.map((invite) => (
                  <TeamMemberInvitationRow
                    key={invite.id}
                    invitation={invite}
                    currentUserIsAdmin={isUserAdmin}
                    onCancelInvite={() => didPressCancelInvite(invite)}
                    handleResendInvite={() => handleResendInvite(invite)}
                  />
                ))}
            </div>
          </>
        ) : null}

        <Styled.Helper>
          <a onClick={() => showIntercomArticle(helpCenterArticles.roles)}>{"Learn more"}</a>
          {" about roles and permissions."}
        </Styled.Helper>
      </AdministrationCard>

      <InviteMembersModalBody
        handleInvite={inviteMembers}
        loading={loading}
        toggle={toggleInvite}
        Modal={InviteMembersModal}
        internalProps={inviteMembersInternal}
      />

      {selectedMember && (
        <>
          <CustomizeTeamMemberModalBody
            handleCustomizeTeamMember={handleCustomizeTeamMember}
            memberName={selectedMember.user.name ?? ""}
            currentMemberRole={selectedMember?.role}
            loading={loading}
            selectedApps={selectedMember?.apps}
            Modal={CustomizeRoleModal}
            internalProps={customizeRoleInternal}
          />

          <DeleteMemberModal
            {...deleteMemberInternal}
            heading={resolveDeleteMemberModalTitle(selectedMember, userData?.id || "")}
            testId={identifiers["members.approvedDomain.dropdownButton.remove.header"]}
            buttonElement={
              <Button
                onClick={() => handleDeleteMember(selectedMember)}
                size="small"
                color="red"
                disabled={loading}
                testId={identifiers["members.approvedDomain.dropdownButton.remove.button"]}
                loadingText={loading && "Removing..."}
              >
                {resolveDeleteMemberModalButtonText(selectedMember, userData?.id || "")}
              </Button>
            }
          >
            <Flex $column>
              <img
                style={{ alignSelf: "center", marginBottom: "2.4rem" }}
                src={WarningImage}
                alt="Warning"
              />
              <Paragraph p1>
                {resolveDeleteMemberModalText(selectedMember, userData?.id || "", selectedWorkspace?.name || "")}
              </Paragraph>
            </Flex>
          </DeleteMemberModal>
        </>
      )}
      {selectedInvite && (
        <CancelInviteModal
          {...cancelInviteInternal}
          heading="Cancel invite?"
          subtitle={`Are your sure you want to cancel invite to ${selectedInvite.email}? Don't worry, Shake won't notify them.`}
          testId={identifiers["members.myTeam.user.pending.dropdownButton.customize.header"]}
          buttonElement={
            <Button
              onClick={() => handleDeleteMemberInvitation(selectedInvite)}
              size="small"
              disabled={loading}
              color="red"
              testId={identifiers["members.myTeam.user.pending.dropdownButton.customize.button"]}
              loadingText={loading && "Canceling invite..."}
            >
              Yes, cancel invite
            </Button>
          }
        />
      )}
    </>
  );
}

export interface TeamMembersGroup {
  id: number;
  name: string;
  color1: string;
  color2?: string;
  members: Member[];
  invitations: Invitation[];
}

const GROUP_ID_AI = 0;
const GROUP_ID_ADMINS = 1;
const GROUP_ID_MEMBERS = 2;

const groupTeamMembers = (
  query: string,
  teamData: WorkspaceData | undefined,
  selectedWorkspace: Workspace | undefined,
  userData: UserData | undefined,
): TeamMembersGroup[] => {
  if (!teamData || !selectedWorkspace || !userData) return [];

  const lowercaseQuery: string = query.toLowerCase();
  const filteredMembers: Member[] = teamData.members.filter(
    (m) => m.user.name.toLowerCase().includes(lowercaseQuery) || m.user.email.toLowerCase().includes(lowercaseQuery),
  );
  const sortedMembers: Member[] = getTeamMembers(selectedWorkspace.role, filteredMembers, userData.id);

  const filteredInvitations: Invitation[] = teamData.invitations.filter((i) =>
    i.email.toLowerCase().includes(lowercaseQuery),
  );
  const sortedInvitations: Invitation[] = getInvitations(selectedWorkspace.role, filteredInvitations);

  const teamMemberGroups: TeamMembersGroup[] = [];

  const aiAgentsGroup: TeamMembersGroup = {
    id: GROUP_ID_AI,
    name: "AI agents",
    color1: "linear-gradient(180deg, #B77DFF 0%, #EB5757 100%)",
    members: [],
    invitations: [],
  };
  teamMemberGroups.push(aiAgentsGroup);

  const adminsGroup: TeamMembersGroup = {
    id: GROUP_ID_ADMINS,
    name: "Admins",
    color1: vars.colors.green,
    members: sortedMembers.filter((m) => m.role === MemberRole.ADMIN),
    invitations: sortedInvitations.filter((i) => i.role === MemberRole.ADMIN) ?? [],
  };
  teamMemberGroups.push(adminsGroup);

  const membersAndGuestsGroup: TeamMembersGroup = {
    id: GROUP_ID_MEMBERS,
    name: "Members and guests",
    color1: vars.colors.orange,
    color2: vars.colors.lavender,
    members: sortedMembers.filter((m) => m.role === MemberRole.MEMBER || m.role === MemberRole.GUEST),
    invitations: sortedInvitations.filter((i) => i.role === MemberRole.MEMBER || i.role === MemberRole.GUEST) ?? [],
  };
  teamMemberGroups.push(membersAndGuestsGroup);

  return teamMemberGroups;
};

function resolveDeleteMemberModalTitle(member: Member, currentUserId: string) {
  return member.user.id === currentUserId ? "Leave the workspace?" : `Remove ${member.user.name}?`;
}

function resolveDeleteMemberModalText(member: Member, currentUserId: string, workspaceName: string) {
  return member.user.id === currentUserId
    ? `You will lose access to all data and tickets in ${workspaceName}.\nTo rejoin in the future, an administrator will need to invite you again. Are you sure you want to leave?`
    : `This will remove ${member.user.email} from the ${workspaceName} workspace.\nThey will immediately lose access to all tickets and data in this workspace.`;
}

function resolveDeleteMemberModalButtonText(member: Member, currentUserId: string) {
  return member.user.id === currentUserId ? "Leave workspace" : `Remove member`;
}

const getTeamMembers = (userRole: MemberRole, members: Member[], currentUserId: string) => {
  if (userRole === MemberRole.GUEST) {
    return sortMembersByName(
      members.filter(
        (member) =>
          member.role !== MemberRole.GUEST || (member.role === MemberRole.GUEST && member.user.id === currentUserId),
      ),
    );
  }
  return sortMembersByName(members);
};

const sortMembersByName = (members: Member[]) => {
  return members.sort((a, b) => {
    if (a.user.name < b.user.name) return -1;
    if (a.user.name > b.user.name) return 1;
    return 0;
  });
};

const getInvitations = (userRole: MemberRole, invitations: Invitation[]) => {
  if (userRole === MemberRole.GUEST) {
    return sortInvitationsByEmail(invitations.filter((invitation) => invitation.role !== MemberRole.GUEST));
  }
  return sortInvitationsByEmail(invitations);
};

const sortInvitationsByEmail = (invitations: Invitation[]) => {
  return invitations.sort((a, b) => {
    if (a.email < b.email) return -1;
    if (a.email > b.email) return 1;
    return 0;
  });
};
